import * as React from "react";
import { Badge, Button, Text, makeStyles, tokens } from "@fluentui/react-components";
import { ChevronDown20Regular, ChevronRight20Regular, Mail20Regular } from "@fluentui/react-icons";
import type { SelectedMailItem } from "../hooks/useSelectedMailItems";

const COLLAPSED_COUNT = 3;

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },

  header: {
    alignItems: "center",
    display: "flex",
    gap: tokens.spacingHorizontalXS,
    justifyContent: "space-between",
  },

  titleRow: {
    alignItems: "center",
    display: "flex",
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
  },

  mutedText: {
    color: tokens.colorNeutralForeground3,
  },

  list: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalXS,
  },

  item: {
    alignItems: "center",
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    boxSizing: "border-box",
    display: "flex",
    gap: tokens.spacingHorizontalXXS,
    maxWidth: "100%",
    minHeight: "28px",
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
  },

  subject: {
    maxWidth: "220px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

interface SelectedEmailPanelProps {
  items: SelectedMailItem[];
}

export const SelectedEmailPanel: React.FC<SelectedEmailPanelProps> = ({ items }) => {
  const styles = useStyles();
  const [expanded, setExpanded] = React.useState(false);
  const visibleItems = expanded ? items : items.slice(0, COLLAPSED_COUNT);
  const hiddenCount = Math.max(items.length - COLLAPSED_COUNT, 0);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <Text weight="semibold" size={200}>
            Selected Emails
          </Text>
          <Badge appearance="filled" color={items.length > 0 ? "brand" : "subtle"} size="small">
            {items.length} selected
          </Badge>
        </div>
        {items.length > COLLAPSED_COUNT && (
          <Button
            appearance="transparent"
            size="small"
            icon={expanded ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? "Show less" : `+${hiddenCount} more`}
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <Text size={200} className={styles.mutedText}>
          Select one or more emails in Outlook to upload them.
        </Text>
      ) : (
        <div className={styles.list}>
          {visibleItems.map((item) => (
            <div className={styles.item} key={item.messageId} title={item.subject}>
              <Mail20Regular />
              <Text size={200} className={styles.subject}>
                {item.subject}
              </Text>
            </div>
          ))}
          {!expanded && hiddenCount > 0 && (
            <div className={styles.item} title={`${hiddenCount} more selected emails`}>
              <Text size={200}>+{hiddenCount} more</Text>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
